import { capitalize, omit } from 'lodash'
import { ExternalLink } from 'shared/components/ExternalLink/ExternalLink'
import { getAdminUrl, isConsole } from 'shared/config/config'
import { ChecklistStatusDetailText } from 'shared/helpers/constants'
import { fieldToTitleCase, toAllSentenceCase } from 'shared/helpers/utils'

import { CreditApplicationStatus } from '../../models/cash'
import {
  LeasingPartnerApplicationTruckAssignmentStatus,
  LeasingPartnerNameLabel,
} from '../../models/leasingPartner'
import {
  BackgroundCheckVendor,
  DriverScorecardStatus,
  OnboardingChecklist,
  OnboardingChecklistName,
  OnboardingChecklistStatusDetail,
  OnboardingSubChecklist,
} from '../../models/onboarding'
import { PlaidAccountStatus } from '../../models/plaid'
import { ProductSubscriptionBundleName } from '../../models/productSubscription'
import { SlimTeam } from '../../models/team'
import { User } from '../../models/user'
import { TaskStatusEnum } from '../TaskList/TaskList'

export function parseOnboardingTaskList(
  onboardingChecklist: ReadonlyArray<OnboardingChecklist>,
  user: User,
  team: SlimTeam,
  getCtaText?: (task: OnboardingChecklist | OnboardingSubChecklist) => string,
  getCta?: (
    task: OnboardingChecklist | OnboardingSubChecklist,
    user: User,
    team: SlimTeam
  ) => (() => string | void) | undefined,
  getCtaRenderer?: (
    task: OnboardingChecklist | OnboardingSubChecklist,
    user: User,
    team: SlimTeam
  ) => React.ReactNode,
  getAdditionalText?: (
    task: OnboardingChecklist | OnboardingSubChecklist
  ) => React.ReactNode | React.ReactNode[]
) {
  return onboardingChecklist
    .map((task) => {
      mutateTaskStatus(task, user, team)
      const title = getTitle(task, user)
      return {
        ...task,
        key: getKey(task),
        name: task.name,
        title,
        titleRenderer: getTitleRenderer(task, user, title),
        status: task.status,
        statusText: ChecklistStatusDetailText[task.statusDetail || 'NONE'] || getStatusText(task, user, team),
        statusAdditionalText: getAdditionalText?.(task),
        subTitle: getSubTitle(task),
        ctaText: getCtaText?.(task),
        onCtaClick: getCta?.(task, user, team),
        ctaRenderer: getCtaRenderer?.(task, user, team),
        isExpanded: getIsExpanded(task),
        subTasks: task.subitems?.map((subtask) => {
          mutateTaskStatus(subtask, user, team)
          const title = getTitle(subtask, user)

          return {
            ...subtask,
            key: getKey(subtask),
            name: subtask.name,
            title,
            titleRenderer: getTitleRenderer(subtask, user, title),
            status: subtask.status,
            statusText:
              ChecklistStatusDetailText[subtask.statusDetail || 'NONE'] || getStatusText(subtask, user, team),
            statusAdditionalText: getAdditionalText?.(subtask),
            subTitle: getSubTitle(subtask),
            ctaText: getCtaText?.(subtask),
            onCtaClick: getCta?.(subtask, user, team),
            ctaRenderer: getCtaRenderer?.(subtask, user, team),
          }
        }),
      }
    })
    .filter((task) => Boolean(task.title))
}

function getTitle(task: OnboardingChecklist | OnboardingSubChecklist, user: User) {
  switch (task.name) {
    case OnboardingChecklistName.BACKGROUND_CHECK:
      return 'Drug screen'

    case OnboardingChecklistName.SIGN_AGREEMENT:
      return 'Sign lease agreement'

    case OnboardingChecklistName.DRIVER_APPLICATION:
      return 'Driver application'

    case OnboardingChecklistName.EMPLOYMENT_HISTORY:
      return 'Employment history'

    case OnboardingChecklistName.FLEET_APPLICATION:
      return 'Fleet application'

    case OnboardingChecklistName.FLEET_DETAILS:
      return 'Fleet details'

    case OnboardingChecklistName.ELD_SETUP:
      return 'ELD setup'

    case OnboardingChecklistName.TRUCK_DECALS:
      return 'Decal setup'

    case OnboardingChecklistName.EQUIPMENT_SETUP:
      return 'Add equipment'

    case OnboardingChecklistName.MAINTENANCE_SET_ASIDE:
      return 'Maintenance fund (optional)'

    case OnboardingChecklistName.ADD_INDIVIDUAL_EQUIPMENT: {
      const equipment = task.context?.truck || task.context?.trailer
      if (!equipment) {
        return 'Equipment'
      }
      return `${equipment.vehicleMake} ${equipment.vehicleModel} ${equipment.vehicleYear}`
    }

    case OnboardingChecklistName.FLEX_AGREEMENT:
      return 'Service & Instant Pay Agreement'

    case OnboardingChecklistName.TAX_INFO:
      return 'W-9 and Taxpayer ID Number'

    case OnboardingChecklistName.ADD_DRIVERS:
      return 'Add driver'

    case OnboardingChecklistName.BROKER_SETUP:
      return 'Broker and load board setup'

    case OnboardingChecklistName.PAYMENT_PROFILE_SETUP:
      return 'CT Cash card'

    case OnboardingChecklistName.BANK_ACCOUNT_INFO:
      return 'Add bank account'

    case OnboardingChecklistName.STRIPE_PAYMENT_PROFILE:
      return 'Verify your identity'

    case OnboardingChecklistName.CREDIT_APPLICATION:
      return 'Apply for credit'

    case OnboardingChecklistName.CONFIRM_CT_CARD_INFO:
      return 'Order card'

    case OnboardingChecklistName.INSURANCE_INFORMATION:
      return 'Occupational accident insurance'

    case OnboardingChecklistName.INSURANCE_BENEFICIARY:
      return 'Provide beneficiary details'

    case OnboardingChecklistName.INSURANCE_SETUP:
      return 'Insurance setup'

    case OnboardingChecklistName.ADD_INDIVIDUAL_INSURANCE_INFORMATION:
      return 'Add insurance information'

    case OnboardingChecklistName.ADD_INDIVIDUAL_BROKER:
      return capitalize(`${task.context?.provider?.name}`)

    case OnboardingChecklistName.FLEX_STARTER_SUBSCRIPTION:
      return 'Billing and subscription'

    case OnboardingChecklistName.CONTRACTOR_PAYMENTS_PROFILE:
      return 'Create payment profile'

    case OnboardingChecklistName.PAYROLL_BANK_ACCOUNT:
      return 'Add payroll bank account'

    case OnboardingChecklistName.ADD_CONTRACTOR:
      return 'Add contractors'

    case OnboardingChecklistName.CONTRACTOR_PERSONAL_PROFILE:
      return 'Confirm personal info'

    case OnboardingChecklistName.CONTRACTOR_PERSONAL_DIRECT_DEPOSIT:
      return 'Add direct deposit account'

    case OnboardingChecklistName.CONTRACTOR_PERSONAL_DEBIT_CARD:
      return 'Add debit card for instant pay'

    case OnboardingChecklistName.ADD_INDIVIDUAL_DRIVER:
      return `${task.context?.user?.firstName} ${task.context?.user?.lastName}${
        task.context?.user?.id === user.id ? ' (you)' : ''
      }`

    case OnboardingChecklistName.SAFETY_REVIEW:
      return 'Safety review'

    case OnboardingChecklistName.WELCOME_PACKAGE:
      return 'Onboarding package'

    case OnboardingChecklistName.ACTIVATION:
      return 'Activation'

    case OnboardingChecklistName.INITIAL_BACKGROUND_CHECK:
      return 'Driving history check'

    case OnboardingChecklistName.CRIMINAL_RECORD_CHECK:
      return 'Criminal record check'

    case OnboardingChecklistName.TRUCK_LEASE_APPLICATION:
      return 'Reserve truck '

    case OnboardingChecklistName.CLEARINGHOUSE_QUERY:
      return 'Clearinghouse check'

    case OnboardingChecklistName.DOT_INSPECTION:
      const dotEquipment = task.context?.truck
      if (!dotEquipment) {
        return 'DOT inspection'
      }

      return `DOT inspection (${dotEquipment.vehicleMake} ${dotEquipment.vehicleModel} ${dotEquipment.vehicleYear})`

    case OnboardingChecklistName.PLATES:
      const platesEquipment = task.context?.truck
      if (!platesEquipment) {
        return 'Plates & registration'
      }

      return `Plates & registration (${platesEquipment.vehicleMake} ${platesEquipment.vehicleModel} ${platesEquipment.vehicleYear})`

    case OnboardingChecklistName.TRUCK_SETUP:
      return 'Equipment documents'

    case OnboardingChecklistName.SIGN_AGREEMENT_AMENDMENT:
      return null // Hide this in UI
  }
}

function getTitleRenderer(
  task: OnboardingChecklist | OnboardingSubChecklist,
  user: User,
  title: string | null
): React.ReactNode {
  switch (task.name) {
    case OnboardingChecklistName.SIGN_AGREEMENT:
      if (!isConsole()) return title
      return (
        <ExternalLink variant="header3" href={getAdminUrl(`/ct-admin/core/esignenvelope/?q=${user.id}`)}>
          {title}
        </ExternalLink>
      )

    case OnboardingChecklistName.CLEARINGHOUSE_QUERY:
      if (!isConsole()) return title
      return (
        <ExternalLink variant="header3" href={getAdminUrl(`/ct-admin/core/clearinghousequery/?q=${user.id}`)}>
          {title}
        </ExternalLink>
      )

    case OnboardingChecklistName.TRUCK_LEASE_APPLICATION:
      if (!isConsole()) return title
      return (
        <ExternalLink variant="header3" href={getAdminUrl(`/ct-admin/core/leasingpartner/?q=${user.id}`)}>
          {title}
        </ExternalLink>
      )

    case OnboardingChecklistName.INITIAL_BACKGROUND_CHECK:
      if (
        task.status === TaskStatusEnum.NOT_STARTED ||
        task.status === TaskStatusEnum.PREREQUISITES_NOT_MET ||
        !isConsole()
      ) {
        return title
      }

      return (
        <ExternalLink variant="header3" href={task.context?.initial_screening_report_url}>
          {title}
        </ExternalLink>
      )

    case OnboardingChecklistName.CRIMINAL_RECORD_CHECK:
      if (
        task.status === TaskStatusEnum.NOT_STARTED ||
        task.status === TaskStatusEnum.PREREQUISITES_NOT_MET
      ) {
        return title
      }

      return (
        <ExternalLink variant="header3" href={task.context?.secondary_screening_report_url}>
          {title}
        </ExternalLink>
      )

    case OnboardingChecklistName.BACKGROUND_CHECK:
      if (
        task.status === TaskStatusEnum.NOT_STARTED ||
        task.status === TaskStatusEnum.PREREQUISITES_NOT_MET ||
        !isConsole()
      ) {
        return title
      }

      return (
        <ExternalLink variant="header3" href={task.context?.secondary_screening_report_url}>
          {title}
        </ExternalLink>
      )

    default:
      return null
  }
}

function getSubTitle(task: OnboardingChecklist | OnboardingSubChecklist) {
  switch (task.name) {
    case OnboardingChecklistName.BACKGROUND_CHECK:
    case OnboardingChecklistName.CRIMINAL_RECORD_CHECK:
      return 'via Checkr'

    case OnboardingChecklistName.CONTRACTOR_PERSONAL_PROFILE:
    case OnboardingChecklistName.CONTRACTOR_PAYMENTS_PROFILE:
      return 'via Astra'

    case OnboardingChecklistName.PAYROLL_BANK_ACCOUNT:
    case OnboardingChecklistName.CONTRACTOR_PERSONAL_DIRECT_DEPOSIT:
      return 'via Plaid'

    case OnboardingChecklistName.STRIPE_PAYMENT_PROFILE:
      return 'via Stripe'

    case OnboardingChecklistName.BANK_ACCOUNT_INFO:
      return 'via Plaid'

    case OnboardingChecklistName.CLEARINGHOUSE_QUERY:
      return 'via FMCSA'

    case OnboardingChecklistName.INSURANCE_SETUP:
      if (task.status === TaskStatusEnum.ERROR) {
        return 'Incomplete'
      }
      return undefined

    default:
      return undefined
  }
}

function getStatusText(task: OnboardingChecklist | OnboardingSubChecklist, user: User, team: SlimTeam) {
  if (task.name === OnboardingChecklistName.TRUCK_SETUP) {
    return ''
  }

  if (
    (task.name === OnboardingChecklistName.BROKER_SETUP ||
      task.name === OnboardingChecklistName.ADD_DRIVERS ||
      task.name === OnboardingChecklistName.PAYMENT_PROFILE_SETUP ||
      task.name === OnboardingChecklistName.EQUIPMENT_SETUP ||
      task.name === OnboardingChecklistName.MAINTENANCE_SET_ASIDE) &&
    task.status !== TaskStatusEnum.PREREQUISITES_NOT_MET
  ) {
    return ''
  }

  if (task.name === OnboardingChecklistName.TRUCK_LEASE_APPLICATION) {
    if (task.status === TaskStatusEnum.COMPLETED) {
      return ''
    }
    if (task.status === TaskStatusEnum.PREREQUISITES_NOT_MET && !isConsole()) {
      return 'Partner recommendation not finalized'
    }
    if (task.status === TaskStatusEnum.NOT_STARTED && !isConsole()) {
      return 'Not started'
    }
    if (task.status === TaskStatusEnum.ERROR) {
      if (task.statusDetail === OnboardingChecklistStatusDetail.REQUEST_DEPOSIT) {
        return 'Pending deposit payment'
      }

      if (task.statusDetail === OnboardingChecklistStatusDetail.DEPOSIT_FAILED) {
        return 'Deposit payment issue'
      }

      if (task.statusDetail === OnboardingChecklistStatusDetail.DEPOSIT_VERIFICATION_NEEDED) {
        return 'Bank account verification required'
      }

      if (task.statusDetail === OnboardingChecklistStatusDetail.PENDING_PROMISSORY_NOTE_SIGNING) {
        return 'Sign promissory note'
      }
      if (task.statusDetail === OnboardingChecklistStatusDetail.PENDING_LEASE_SIGNING) {
        return 'Sign truck leasing agreement'
      }
    }
    if (task.statusDetail === OnboardingChecklistStatusDetail.DEPOSIT_COMPLETE) {
      return 'Truck assignment pending'
    }
    if (task.statusDetail === OnboardingChecklistStatusDetail.PENDING_LEASE_SIGNING) {
      return `${'Truck'} pending pickup`
    }
    if (task.statusDetail === OnboardingChecklistStatusDetail.PENDING_PROMISSORY_NOTE_SIGNING) {
      return 'Sign promissory note'
    }
    if (task.statusDetail === OnboardingChecklistStatusDetail.DEPOSIT_PENDING) {
      return 'Deposit payment processing'
    }

    if (!task.context?.leasingPartner?.suggestedPartnerName && !task.context?.leasingPartner?.partnerName) {
      if (isConsole() && task.context?.driverScorecard?.status === DriverScorecardStatus.PENDING_REVIEW) {
        return 'Review needed'
      }
      return 'Partner matching in progress'
    }
    if (
      task.context?.leasingPartner?.suggestedPartnerName &&
      !task.context?.leasingPartner?.partnerName &&
      isConsole()
    ) {
      return `
          Recommended: ${LeasingPartnerNameLabel[task.context?.leasingPartner?.suggestedPartnerName]}
          Pending driver’s selection
        `
    }
    if (
      task.context?.leasingPartner?.suggestedPartnerName &&
      task.context?.leasingPartner?.partnerName &&
      isConsole()
    ) {
      return `
          ${task.context?.leasingPartner?.application?.truckAssignmentStatus === LeasingPartnerApplicationTruckAssignmentStatus.READY_TO_BE_ASSIGNED ? 'Truck assignment required.' : ''}
          Recommended: ${LeasingPartnerNameLabel[task.context?.leasingPartner?.suggestedPartnerName]}
          Driver’s selection:
          ${LeasingPartnerNameLabel[task.context?.leasingPartner?.partnerName]}
        `
    }
    return isConsole() ? 'Review needed for matching' : 'In progress'
  }

  switch (task.status) {
    case TaskStatusEnum.NOT_STARTED: {
      if (task.name === OnboardingChecklistName.FLEX_AGREEMENT) {
        return 'Check your e-mail for a DocuSign link'
      }
      if (task.name === OnboardingChecklistName.SIGN_AGREEMENT) {
        if (user.featureFlags?.publicInAppSignLeaseAgreement) return 'Not started'
        return 'Check your e-mail for a DocuSign link'
      }
      if (
        task.name === OnboardingChecklistName.CLEARINGHOUSE_QUERY &&
        task.statusDetail === OnboardingChecklistStatusDetail.CONSENT_PENDING
      ) {
        return 'Pending consent'
      }
      return 'Not started'
    }

    case TaskStatusEnum.IN_PROGRESS: {
      if (task.name === OnboardingChecklistName.ADD_INDIVIDUAL_DRIVER) return 'Onboarding In Progress'
      if (
        task.name === OnboardingChecklistName.SIGN_AGREEMENT &&
        user.featureFlags?.publicInAppSignLeaseAgreement
      )
        return 'In progress'
      if (task.name === OnboardingChecklistName.PLATES) return 'Truck registration in review'
      if (
        task.name === OnboardingChecklistName.DRIVER_APPLICATION ||
        task.name === OnboardingChecklistName.FLEX_AGREEMENT ||
        task.name === OnboardingChecklistName.SIGN_AGREEMENT ||
        task.name === OnboardingChecklistName.FLEET_DETAILS ||
        task.name === OnboardingChecklistName.ADD_INDIVIDUAL_EQUIPMENT ||
        task.name === OnboardingChecklistName.INSURANCE_INFORMATION ||
        task.name === OnboardingChecklistName.EMPLOYMENT_HISTORY ||
        task.name === OnboardingChecklistName.TRUCK_DECALS ||
        task.name === OnboardingChecklistName.DOT_INSPECTION
      )
        return 'In review'

      if (task.name === OnboardingChecklistName.INSURANCE_SETUP) {
        return 'In review'
      }

      if (task.name === OnboardingChecklistName.CREDIT_APPLICATION) {
        if (team.creditStatus === CreditApplicationStatus.APPROVED)
          return 'Credit extended, pending acceptance'
        return 'In review'
      }
      if (
        task.name === OnboardingChecklistName.ADD_INDIVIDUAL_BROKER ||
        task.name === OnboardingChecklistName.ELD_SETUP
      ) {
        return `Link pending`
      }
      if (task.name === OnboardingChecklistName.BANK_ACCOUNT_INFO) {
        if (
          task.context?.plaidAccount?.verificationStatus === PlaidAccountStatus.PENDING_MANUAL_VERIFICATION
        ) {
          return `Depositing in progress`
        }
        return `Verification in progress`
      }
      if (
        task.name === OnboardingChecklistName.CONFIRM_CT_CARD_INFO ||
        task.name === OnboardingChecklistName.STRIPE_PAYMENT_PROFILE
      ) {
        return `Action required`
      }

      if (task.name === OnboardingChecklistName.BACKGROUND_CHECK) {
        if (task.statusDetail === OnboardingChecklistStatusDetail.REVIEW_NEEDED) {
          return 'Review needed'
        }
        if (task.statusDetail === OnboardingChecklistStatusDetail.SCREENING_APPOINTMENT_ATTENDED) {
          return 'Appointment attended'
        }
        if (task.context?.backgroundCheckVendor === BackgroundCheckVendor.CHECKR) {
          return `Scheduled`
        }
      }

      if (task.name === OnboardingChecklistName.INITIAL_BACKGROUND_CHECK) {
        if (task.statusDetail) {
          if (
            !isConsole() &&
            (task.statusDetail === OnboardingChecklistStatusDetail.REPORT_ORDERED ||
              task.statusDetail === OnboardingChecklistStatusDetail.REVIEW_NEEDED)
          ) {
            return 'In review - may take a few hours'
          }
          return toAllSentenceCase(fieldToTitleCase(task.statusDetail))
        }
        return isConsole() ? 'Report ordered' : 'In review - may take a few hours'
      }

      if (task.name === OnboardingChecklistName.CLEARINGHOUSE_QUERY) {
        return 'Pending result'
      }

      if (task.name === OnboardingChecklistName.CRIMINAL_RECORD_CHECK) {
        if (task.statusDetail) {
          return toAllSentenceCase(fieldToTitleCase(task.statusDetail))
        }
        return 'Report ordered'
      }

      if (task.name === OnboardingChecklistName.WELCOME_PACKAGE) {
        if (user.featureFlags?.publicEldOnboarding) {
          if (
            task.statusDetail === OnboardingChecklistStatusDetail.CRIMINAL_RECORD_CHECK_NOT_COMPLETED &&
            isConsole()
          ) {
            return 'Criminal record check not completed'
          }

          if (task.statusDetail === OnboardingChecklistStatusDetail.SHIPPED) {
            return 'In transit'
          }

          if (task.statusDetail === OnboardingChecklistStatusDetail.DELIVERED) {
            return 'Delivered'
          }

          return 'Waiting to ship'
        }
      }

      return 'In progress'
    }

    case TaskStatusEnum.COMPLETED: {
      if (task.name === OnboardingChecklistName.ADD_INDIVIDUAL_DRIVER) return 'Joined'

      if (
        task.name === OnboardingChecklistName.FLEX_AGREEMENT ||
        task.name === OnboardingChecklistName.SIGN_AGREEMENT
      ) {
        if (user.featureFlags?.publicInAppSignLeaseAgreement) return 'Completed'
        return 'Completed on DocuSign'
      }

      return 'Completed'
    }

    case TaskStatusEnum.PREREQUISITES_NOT_MET: {
      if (
        [
          OnboardingChecklistName.EMPLOYMENT_HISTORY,
          OnboardingChecklistName.CLEARINGHOUSE_QUERY,
          OnboardingChecklistName.BACKGROUND_CHECK,
        ].includes(task.name)
      ) {
        return ''
      }
      if (task.name === OnboardingChecklistName.CRIMINAL_RECORD_CHECK) {
        return ''
      }

      if (task.name === OnboardingChecklistName.WELCOME_PACKAGE) {
        if (user.featureFlags?.publicEldOnboarding) {
          return ''
        }
        return 'CloudTrucks will send this to you'
      }
      if (task.name === OnboardingChecklistName.SIGN_AGREEMENT) {
        return ''
      }
      if (task.name === OnboardingChecklistName.EQUIPMENT_SETUP) {
        return ''
      }
      if (
        task.name === OnboardingChecklistName.BROKER_SETUP ||
        task.name === OnboardingChecklistName.PAYMENT_PROFILE_SETUP ||
        task.name === OnboardingChecklistName.TAX_INFO ||
        task.name === OnboardingChecklistName.ADD_DRIVERS
      ) {
        if (task.name === OnboardingChecklistName.PAYMENT_PROFILE_SETUP) {
          if (!team.isTaxInfoComplete) return 'W9 or Tax ID is missing, contact your onboarding specialist'
          return 'Complete sign lease agreement'
        }
        if (team.productSubscriptionName === ProductSubscriptionBundleName.PREMIUM) {
          return 'Complete Sign Lease Agreement'
        }

        if (team.isFlexStarter) {
          return 'Complete Billing and Subscription'
        }

        return 'Complete Service & Instant Pay Agreement'
      }

      if (task.name === OnboardingChecklistName.CREDIT_APPLICATION)
        return 'Add bank account, verify your identity'

      if (task.name === OnboardingChecklistName.CONFIRM_CT_CARD_INFO) {
        if (task.statusDetail === OnboardingChecklistStatusDetail.NOT_CASH_USER)
          return 'Contact the first admin on your fleet to order'
        return 'Add bank account, verify your identity'
      }

      if (
        task.name === OnboardingChecklistName.PAYROLL_BANK_ACCOUNT ||
        task.name === OnboardingChecklistName.ADD_CONTRACTOR
      ) {
        return 'Complete payment profile'
      }

      if (
        task.name === OnboardingChecklistName.CONTRACTOR_PERSONAL_DIRECT_DEPOSIT ||
        task.name === OnboardingChecklistName.CONTRACTOR_PERSONAL_DEBIT_CARD
      ) {
        return 'Confirm personal info'
      }

      if (task.name === OnboardingChecklistName.INITIAL_BACKGROUND_CHECK) {
        if (task.statusDetail) {
          return toAllSentenceCase(fieldToTitleCase(task.statusDetail))
        }
        return 'Not started'
      }

      return 'Not started'
    }

    case TaskStatusEnum.ERROR:
      if (task.name === OnboardingChecklistName.ADD_INDIVIDUAL_EQUIPMENT) {
        return 'Missing information'
      }
      if (task.name === OnboardingChecklistName.BANK_ACCOUNT_INFO) {
        return `Account linking error`
      }
      if (task.name === OnboardingChecklistName.DRIVER_APPLICATION) {
        if (task.statusDetail === OnboardingChecklistStatusDetail.MISSING_BG_CHECK) {
          return `Consent needed`
        }
        return `Missing information`
      }
      if (
        task.name === OnboardingChecklistName.CREDIT_APPLICATION ||
        task.name === OnboardingChecklistName.EMPLOYMENT_HISTORY
      ) {
        return `Missing information`
      }
      if (task.name === OnboardingChecklistName.FLEET_APPLICATION) {
        if (task.context?.fleetApplication) {
          const fleetApplication = Object.values(
            omit(task.context?.fleetApplication, [
              'businessType',
              team.productSubscriptionName === ProductSubscriptionBundleName.CASH
                ? 'certificateOfInsurance'
                : '',
            ])
          )
          return `${fleetApplication.filter(Boolean).length} of ${fleetApplication.length} Completed`
        }
        return ''
      }
      if (
        task.name === OnboardingChecklistName.CRIMINAL_RECORD_CHECK ||
        task.name === OnboardingChecklistName.BACKGROUND_CHECK
      ) {
        if (task.statusDetail === OnboardingChecklistStatusDetail.REPORT_EXPIRED) {
          return 'Report expired, reorder needed'
        }
        return ''
      }

      if (task.name === OnboardingChecklistName.INITIAL_BACKGROUND_CHECK) {
        if (task.statusDetail) {
          if (task.statusDetail === OnboardingChecklistStatusDetail.REPORT_EXPIRED) {
            return 'Report expired, reorder needed'
          }
          return toAllSentenceCase(fieldToTitleCase(task.statusDetail))
        }
        return ''
      }

      if (task.name === OnboardingChecklistName.CLEARINGHOUSE_QUERY && isConsole()) {
        if (task.statusDetail === OnboardingChecklistStatusDetail.CLEARINGHOUSE_EXPIRED) {
          return 'Query expired, reorder needed'
        }
        return task.statusDetail || ''
      }

      return ''

    default:
      return ''
  }
}

function getKey(task: OnboardingChecklist | OnboardingSubChecklist) {
  switch (task.name) {
    case OnboardingChecklistName.ADD_INDIVIDUAL_EQUIPMENT: {
      return task?.context?.truck?.id || task?.context?.trailer?.id
    }
    default:
      return ''
  }
}

function getIsExpanded(task: OnboardingChecklist | OnboardingSubChecklist) {
  if (task.name === OnboardingChecklistName.PAYMENT_PROFILE_SETUP) {
    const allSubtasksCompleted = (task as OnboardingChecklist)?.subitems.every(
      (item) => item.status === TaskStatusEnum.COMPLETED
    )

    if (allSubtasksCompleted) return false
    return (task as OnboardingChecklist)?.subitems.some((item) => item.status === TaskStatusEnum.COMPLETED)
  }

  if (task.name === OnboardingChecklistName.EQUIPMENT_SETUP) {
    return (task as OnboardingChecklist).subitems?.length > 0
  }

  return undefined
}

function mutateTaskStatus(task: OnboardingChecklist | OnboardingSubChecklist, user: User, team: SlimTeam) {
  if (task.name === OnboardingChecklistName.PAYMENT_PROFILE_SETUP) {
    const bankAccount = (task as OnboardingChecklist).subitems?.find(
      (item) => item.name === OnboardingChecklistName.BANK_ACCOUNT_INFO
    )
    const paymentProfile = (task as OnboardingChecklist).subitems?.find(
      (item) => item.name === OnboardingChecklistName.STRIPE_PAYMENT_PROFILE
    )
    const ctCash = (task as OnboardingChecklist).subitems?.find(
      (item) => item.name === OnboardingChecklistName.CONFIRM_CT_CARD_INFO
    )
    const creditTask = (task as OnboardingChecklist).subitems?.find(
      (t) => t.name === OnboardingChecklistName.CREDIT_APPLICATION
    )
    const hasCreditTask = Boolean(creditTask)
    const isCreditOnly = team.isCreditOnly

    if (
      bankAccount?.status === TaskStatusEnum.COMPLETED &&
      paymentProfile?.status === TaskStatusEnum.COMPLETED &&
      creditTask?.status === TaskStatusEnum.PREREQUISITES_NOT_MET
    ) {
      creditTask.statusDetail = OnboardingChecklistStatusDetail.CONTACT_CLOUDTRUCKS_REPRESENTATIVE
    }

    if (
      hasCreditTask &&
      creditTask?.status !== TaskStatusEnum.COMPLETED &&
      ctCash?.status === TaskStatusEnum.PREREQUISITES_NOT_MET
    ) {
      ctCash.statusDetail = OnboardingChecklistStatusDetail.COMPLETE_PREVIOUS_TASKS
    } else if (isCreditOnly && !hasCreditTask && ctCash?.status === TaskStatusEnum.PREREQUISITES_NOT_MET) {
      ctCash.statusDetail = OnboardingChecklistStatusDetail.FLEET_NOT_ELIGIBLE
    } else if (
      bankAccount?.status === TaskStatusEnum.COMPLETED &&
      paymentProfile?.status === TaskStatusEnum.COMPLETED &&
      ctCash?.status === TaskStatusEnum.PREREQUISITES_NOT_MET
    ) {
      ctCash.statusDetail = OnboardingChecklistStatusDetail.CONTACT_CLOUDTRUCKS_REPRESENTATIVE
    }
  }

  if (task.name === OnboardingChecklistName.PAYMENT_PROFILE_SETUP) {
    const subtasksWithoutStripe = (task as OnboardingChecklist).subitems.filter(
      (t) => t.name !== OnboardingChecklistName.STRIPE_PAYMENT_PROFILE
    )
    const stripeTask = (task as OnboardingChecklist).subitems.find(
      (t) => t.name !== OnboardingChecklistName.STRIPE_PAYMENT_PROFILE
    )
    const subtasksWithoutStripeNotStarted = subtasksWithoutStripe.every(
      (t) => t.status === TaskStatusEnum.PREREQUISITES_NOT_MET || t.status === TaskStatusEnum.NOT_STARTED
    )

    if (
      subtasksWithoutStripeNotStarted &&
      task.status !== TaskStatusEnum.PREREQUISITES_NOT_MET &&
      (stripeTask?.status === TaskStatusEnum.NOT_STARTED || stripeTask?.status === TaskStatusEnum.IN_PROGRESS)
    ) {
      task.status = TaskStatusEnum.NOT_STARTED
    }
  }

  // Payment profile returns by default in progress state (due to STRIPE integration) but we should show not started icon for that
  if (
    (task.name === OnboardingChecklistName.CONFIRM_CT_CARD_INFO ||
      task.name === OnboardingChecklistName.STRIPE_PAYMENT_PROFILE) &&
    task.status === TaskStatusEnum.IN_PROGRESS
  ) {
    task.status = TaskStatusEnum.NOT_STARTED
  }
}
